<template>
  <div class="grammar-assessment" @copy="event_cancellation">
    <!-- Part 1: MCQs -->
    <template v-if="!mcqsCompleted">
      <v-row class="mt-6">
        <!-- <div class="col-12">
          <div v-html="$t('candidate.assessments.mcqs-instructions')"></div>
        </div> -->
        <div class="col-12">
          <p class="base-body-text dark--text my-8">
            <b>Task A: Grammar Correction/Completion</b>
          </p>
          <p>
            Choose the most grammatically correct word/phrase to complete the
            following sentences.
          </p>
        </div>

        <div class="mcqs-section">
          <v-col
            v-for="(question, index) in app_eng_assessment.mcqs"
            :key="index"
            cols="12"
            md="12"
            class="ml-25"
          >
            <b>{{ $t("shared.question") }} {{ index + 1 }}</b>
            <div class="response-card">
              <b
                >{{ question.question_text }} <span class="required">*</span></b
              >
              <template v-if="question.question_options.options?.length">
                <div
                  class="d-flex align-center flex-md-row justify-between mt-5"
                  style="gap: 0.5rem; width: 100%; flex-wrap: wrap"
                >
                  <template
                    v-for="(option, op_index) in question.question_options
                      .options"
                  >
                    <div
                      :key="op_index"
                      class="d-flex flex-column align-items-center flex-md-row flex-grow-1 flex-wrap"
                      style="gap: 0.5rem"
                    >
                      <v-btn
                        @click="
                          process_ans(
                            question.id,
                            index,
                            question.question_text,
                            option,
                            question.question_options.answer
                          )
                        "
                        class="op-btn base-body-text flex-grow-1 op-btn-v3"
                        style="text-transform: none"
                        :class="[
                          options[index] && options[index].answer === option
                            ? 'selected'
                            : ''
                        ]"
                        solo
                        elevation="0"
                      >
                        {{ option }}
                      </v-btn>
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </v-col>
          <div class="mt-10 text-end" style="width: 100%">
            <v-btn
              width="150px"
              color="primary"
              :loading="submit_ans_loading"
              :disabled="disabled_mcqs_complete"
              :class="{
                'base-submit-button': !disabled_mcqs_complete,
                'base-disable-button': disabled_mcqs_complete
              }"
              @click="completeMcqsAndMoveToWritten"
            >
              {{ $t("shared.next") }}
            </v-btn>
          </div>
        </div>
      </v-row>
    </template>

    <!-- Part 2: Written Assessment (Grammar Correction) -->
    <template v-else>
      <AppEngWrittenAssessment
        :assessment_type="assessment_type"
        :restored_state="writtenRestoredState"
        @assessment_completed="$emit('assessment_completed')"
        @question_answered="$emit('question_answered', $event)"
        @state_changed="$emit('state_changed', $event)"
      />
    </template>
  </div>
</template>

<script>
import { ViewAssessmentsOptions } from "@/interfaces/candidate/canidate_assessments";
import {
  ASSESSMENT_AVERAGE_TIME_STATS,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH,
  ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE,
  SUBMIT_APP_ASSESSMENT_ANSWERS
} from "@/store/modules/candidates/constants";
import { ROOT_ERROR } from "@/store/modules/root/constants";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import AppEngWrittenAssessment from "./AppEngWrittenAssessment.vue";

export default Vue.extend({
  name: "AppEngGrammarAssessment",
  components: {
    AppEngWrittenAssessment
  },
  props: {
    assessment_type: { type: String, required: true },
    restored_state: { type: Object, required: false, default: null },
    writtenRestoredState: { type: Object, required: false, default: null }
  },
  data() {
    return {
      mcqsCompleted: false,
      answeredMcqsCount: 0,
      disabled_mcqs_complete: true,
      options: [],
      submit_ans_loading: false,
      current_question_index: 0,
      partTime: 0,
      questionTime: 0
    };
  },
  mounted() {
    // Restore from saved state if available
    if (this.restored_state) {
      this.restoreFromSavedState();
    }

    // #region agent log
    fetch("http://127.0.0.1:7243/ingest/13206365-9101-46c2-a8d8-ea23c925c398", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "d2ab8d"
      },
      body: JSON.stringify({
        sessionId: "d2ab8d",
        runId: "pre-fix",
        hypothesisId: "H2",
        location: "AppEngGrammarAssessment.vue:mounted",
        message: "Grammar mounted",
        data: {
          assessment_type: this.assessment_type,
          mcqsCount: this.app_eng_assessment?.mcqs?.length || 0,
          filter: this.app_eng_assessment?.filter
        },
        timestamp: Date.now()
      })
    }).catch(() => {
      // Log not critical
    });
    // #endregion agent log
  },
  watch: {
    restored_state: {
      handler(newVal) {
        if (newVal && (newVal.mcqsCompleted !== undefined || newVal.options)) {
          this.restoreFromSavedState();
        }
      },
      deep: true
    },
    // Emit state changes for persistence
    options: {
      handler() {
        this.emitStateChange();
      },
      deep: true
    },
    mcqsCompleted() {
      this.emitStateChange();
    }
  },
  computed: {
    ...mapGetters("candidate", {
      app_eng_assessment: ASSESSMENT_QUESTIONNAIRES_ENGLISH,
      get_store_data: ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE
    })
  },
  methods: {
    ...mapMutations("candidate", {
      set_app_eng_assessments: ASSESSMENT_QUESTIONNAIRES_ENGLISH,
      update_app_eng_assessment_questions:
        ASSESSMENT_QUESTIONNAIRES_ENGLISH_UPDATE
    }),
    ...mapMutations({
      set_root_error: ROOT_ERROR
    }),
    ...mapActions("candidate", {
      submit_assessment_answers: SUBMIT_APP_ASSESSMENT_ANSWERS,
      set_assessment_average_time: ASSESSMENT_AVERAGE_TIME_STATS
    }),
    event_cancellation(event) {
      event.preventDefault();
    },
    /**
     * Restore state from saved session
     */
    restoreFromSavedState() {
      if (!this.restored_state) return;

      if (this.restored_state.mcqsCompleted !== undefined) {
        this.mcqsCompleted = this.restored_state.mcqsCompleted;
      }
      if (this.restored_state.options) {
        this.options = [...this.restored_state.options];
        this.answeredMcqsCount = this.options.filter(
          (item) => item !== undefined && item !== null
        )?.length;

        // Check if all MCQs are answered
        const arrayFull = Array.from({
          length: this.app_eng_assessment.mcqs?.length
        }).every((_, index) => this.options[index] !== undefined);
        this.disabled_mcqs_complete = !arrayFull;
      }

      console.log("Grammar section state restored");
    },
    /**
     * Emit current state for persistence
     */
    emitStateChange() {
      this.$emit("state_changed", {
        section: "grammar",
        state: {
          mcqsCompleted: this.mcqsCompleted,
          options: this.options,
          answeredMcqsCount: this.answeredMcqsCount
        }
      });
    },
    async process_ans(
      question_id,
      question_index,
      question_text,
      answer,
      correct_answer
    ) {
      // Emit answered question id for global progress tracking (english_v4-style)
      if (
        (this.assessment_type === "english_v4" ||
          this.assessment_type === "english_v5" ||
          this.assessment_type === "english_genx_easy_v1" ||
          this.assessment_type === "english_genx_medium_v1" ||
          this.assessment_type === "english_technician_v1" ||
          this.assessment_type === "english_lead_technician_v1" ||
          this.assessment_type === "english_assistant_engineer_v1") &&
        this.app_eng_assessment.mcqs[this.current_question_index]
      ) {
        this.$emit(
          "question_answered",
          this.app_eng_assessment.mcqs[this.current_question_index].id
        );
      }
      this.submit_ans_loading = true;
      const current_result = answer === correct_answer ? true : false;
      this.options[question_index] = {
        question: question_text,
        question_index: question_index,
        answer: answer,
        result: current_result
      };

      this.answeredMcqsCount = this.options.filter(
        (item) => item !== undefined && item !== null
      )?.length;

      const arrayFull = Array.from({
        length: this.app_eng_assessment.mcqs?.length
      }).every((_, index) => this.options[index] !== undefined);

      this.disabled_mcqs_complete = !arrayFull;

      const payload = {
        filter: ViewAssessmentsOptions.GRAMMAR,
        answer: answer,
        question_id: question_id
      };

      localStorage.setItem("stage", ViewAssessmentsOptions.GRAMMAR);
      localStorage.setItem("question_id", payload.question_id.toString());
      localStorage.setItem("answers", JSON.stringify(this.options));

      this.set_app_eng_assessments({
        filter: ViewAssessmentsOptions.GRAMMAR,
        mcqs_completed: arrayFull,
        mcqs: this.app_eng_assessment.mcqs,
        written: this.app_eng_assessment.written,
        reading_questions: this.app_eng_assessment.reading_questions,
        current_question_index: question_index,
        id: this.app_eng_assessment.id,
        assessment_type: this.app_eng_assessment.assessment_type
      });

      this.update_app_eng_assessment_questions(payload);

      const mcqs_api_payload = {
        answer: answer,
        question_id: question_id,
        answer_type: this.app_eng_assessment.mcqs[question_index].question_type,
        assessment_id:
          this.app_eng_assessment.mcqs[question_index].assessment_id
      };

      const response = await this.submit_assessment_answers(mcqs_api_payload);

      this.set_assessment_average_time({
        answer_time: this.questionTime.toString()
      });
      this.questionTime = 0;

      if (!response) {
        this.set_root_error(`${this.$t("assessments.went-wrong")}`);
        this.submit_ans_loading = false;
        return;
      }

      this.submit_ans_loading = false;
    },
    completeMcqsAndMoveToWritten() {
      this.mcqsCompleted = true;
      this.set_assessment_average_time({
        mcqs_time: this.partTime.toString()
      });
      this.questionTime = 0;
      this.partTime = 0;
      localStorage.setItem("stage", ViewAssessmentsOptions.GRAMMAR);
      localStorage.removeItem("answers");
      window.scrollTo({
        top: 0,
        behavior: "smooth" // optional, smooth scroll
      });

      // #region agent log
      fetch(
        "http://127.0.0.1:7243/ingest/13206365-9101-46c2-a8d8-ea23c925c398",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Debug-Session-Id": "d2ab8d"
          },
          body: JSON.stringify({
            sessionId: "d2ab8d",
            runId: "pre-fix",
            hypothesisId: "H2",
            location:
              "AppEngGrammarAssessment.vue:completeMcqsAndMoveToWritten",
            message: "MCQs completed, moving to written",
            data: {
              assessment_type: this.assessment_type,
              mcqsCompleted: this.mcqsCompleted,
              answeredMcqsCount: this.answeredMcqsCount
            },
            timestamp: Date.now()
          })
        }
      ).catch(() => {
        // Log not critical
      });
      // #endregion agent log
    },
    send_mcqs_response() {
      // Time over handler - complete all remaining as-is
      this.completeMcqsAndMoveToWritten();
    }
  }
});
</script>

<style scoped lang="scss">
.grammar-assessment {
  padding: 20px;
}
.op-btn-v3 {
  border: 1px solid #e0e0e0;
  padding: 10px 15px;
  &.selected {
    background-color: var(--v-primary-base) !important;
    color: white !important;
  }
}
.response-card {
  background: #f7f7f7;
  padding: 15px;
  border-radius: 6px;
  margin-top: 10px;
  .required {
    color: red;
  }
}
.mcqs-section {
  width: 100%;
}
.time-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f5f5f5;
  border-radius: 8px;
}
.mcqs-section {
  margin: auto;
}
</style>
